import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';

// Single shared source of truth for white-label branding — written by whichever
// page's Associate Company filter is currently on screen, read by
// SidenavNew/NavbarNew to pick what to show:
//   - `logo`    -> rendered top-left, in the navbar.
//   - `favicon` -> rendered bottom-left, in the collapsed sidenav corner.
// (Naming matches the two fields the backend added to Company/Associate
// Company objects — `favicon` here is this app's own small-corner-mark image,
// not the browser tab favicon.)
//
// `logo` has three distinct states:
//   - undefined -> not resolved yet (still loading)               -> render nothing
//   - null      -> resolved, no logo anywhere in the chain         -> render the ezyCOMP default
//   - string    -> resolved, this is the real logo URL             -> render it
//
// `favicon` has FOUR — it additionally distinguishes WHY it's blank, because
// unlike logo, a blank favicon does NOT always mean "show the ezyCOMP
// default": that only applies when there's no white-label CONTEXT at all
// (whitelabeling_enabled is false, or no entity — e.g. useDefaultBrandingOnly
// pages). A real, enabled company/AC that simply hasn't uploaded its own
// favicon should render blank, not the default (per the white-labelling test
// matrix, e.g. row 4/6/7/8: "favicon left blank", not "ezyCOMP favicon").
//   - undefined -> not resolved yet                                -> render nothing
//   - null      -> no white-label context at all                   -> render the ezyCOMP default
//   - ''        -> a real, enabled entity resolved, but it (and any -> render nothing
//                  parent it inherits from) has no favicon of its own
//   - string    -> resolved, this is the real favicon URL           -> render it
// Only the initial value is `undefined`; setAssociateCompanyBranding never
// writes back to it — once a page has resolved something (even to "nothing to
// show"), we stop treating it as "still loading".

interface BrandableEntity {
  logo?: string | null;
  favicon?: string | null;
  whitelabeling_enabled?: boolean;
  // Present on real API records — used only for the parent-fallback rule
  // below, never required of a caller.
  is_parent?: boolean;
  parent_company_id?: string | null;
  // Only present on the tree-shaped /api/v1/companies/user_companies
  // response's top-level company records — nested associate_companies items
  // never carry this. See the isParent comment below for why this matters.
  associate_companies?: any[];
}

interface AssociateCompanyContextType {
  logo: string | null | undefined;
  favicon: string | null | undefined;
  // Pass the raw company/associate-company object (or null/undefined to reset
  // to "nothing to show"). Internally applies the whitelabeling_enabled gate —
  // callers never need to check that flag themselves, and never need to fetch
  // or touch the image data beyond passing the object through: if
  // whitelabeling_enabled is false, both logo and favicon resolve to null
  // (the default ezyCOMP images) regardless of whether logo/favicon URLs are
  // present on the object.
  setAssociateCompanyBranding: (entity: BrandableEntity | null | undefined) => void;
}

const AssociateCompanyContext = createContext<AssociateCompanyContextType | undefined>(undefined);

export function AssociateCompanyProvider({ children }: { children: ReactNode }) {
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [favicon, setFavicon] = useState<string | null | undefined>(undefined);

  // Remembers the most recently branded PARENT company (is_parent, or no
  // parent_company_id) so a later call for one of ITS associate companies can
  // fall back to it. Confirmed rule (per the white-labelling test matrix):
  //   - An associate company with NO logo of its own inherits BOTH the
  //     parent's logo AND favicon — it hasn't set up any branding of its own,
  //     so it's fully riding on the parent's.
  //   - An associate company WITH its own logo is fully self-managed from
  //     then on: if it has no favicon of its own, that favicon is left blank
  //     (the default) — it does NOT fall back to the parent's favicon. Only a
  //     completely un-branded associate company inherits; a partially-branded
  //     one doesn't get topped up field-by-field.
  // Deliberately keyed on the associate company's OWN logo presence, not on
  // is_copied/name matching — is_copied is unreliable (a since-found
  // bulk-import bug stamped it on many real, distinct companies that are not
  // clones of their parent), so it's not used here at all.
  const lastParentRef = useRef<{ logo?: string | null; favicon?: string | null } | null>(null);

  function setAssociateCompanyBranding(entity: BrandableEntity | null | undefined) {
    if (!entity || entity.whitelabeling_enabled === false) {
      setLogo(null);
      setFavicon(null);
      return;
    }

    // Detecting "is this the parent" can't rely on is_parent/parent_company_id
    // alone — confirmed from a real API response that the tree-shaped
    // /api/v1/companies/user_companies endpoint omits BOTH fields on every
    // record, at both levels. `!entity.parent_company_id` used to be treated
    // as "so this must be the parent", which is wrong when it's just absent:
    // it misclassified associate companies from that endpoint as parents too,
    // clobbering lastParentRef with the AC's own (blank) logo/favicon and
    // skipping inheritance entirely. Array.isArray(associate_companies) is a
    // reliable structural signal instead — only a top-level company record in
    // that tree shape carries it; a nested associate company never does.
    const isParent = entity.is_parent === true || Array.isArray(entity.associate_companies);
    if (isParent) {
      lastParentRef.current = { logo: entity.logo, favicon: entity.favicon };
    }

    const parent = lastParentRef.current;
    const inheritFromParent = !isParent && !entity.logo && !!parent;

    setLogo(entity.logo || (inheritFromParent ? parent!.logo : null) || null);
    // '' (not null) as the final fallback — a real, enabled entity resolved,
    // it just has no favicon anywhere in its chain. null is reserved for "no
    // white-label context at all" (the branch above), which is the only case
    // that should show the ezyCOMP default favicon.
    setFavicon(entity.favicon || (inheritFromParent ? parent!.favicon : null) || '');
  }

  return (
    <AssociateCompanyContext.Provider value={{ logo, favicon, setAssociateCompanyBranding }}>
      {children}
    </AssociateCompanyContext.Provider>
  );
}

export function useAssociateCompanyLogo() {
  const context = useContext(AssociateCompanyContext);
  if (context === undefined) {
    throw new Error('useAssociateCompanyLogo must be used within an AssociateCompanyProvider');
  }
  return context;
}

// For pages where white-label branding should NEVER apply — internal/admin
// tooling (e.g. Super Admin's Input Module setup/upload pages, schedule
// import/export screens) that isn't scoped to any one client, even though it
// may have its own Company/Associate Company filter for other purposes. Call
// once at the top of such a page's component: it forces the sidenav/navbar
// back to the default ezyCOMP logo and favicon for as long as the page stays
// mounted, overriding whatever branding was showing from wherever the user
// navigated from. Pages using this must NOT also call
// setAssociateCompanyBranding from their own filter logic — otherwise that
// would fight this and win once the filter resolves.
export function useDefaultBrandingOnly() {
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();
  useEffect(() => {
    setAssociateCompanyBranding(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
